import Add from '@mui/icons-material/Add';
import Clear from '@mui/icons-material/Clear';
import Edit from '@mui/icons-material/Edit';
import Mail from '@mui/icons-material/Mail';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Sms from '@mui/icons-material/Sms';
import ToggleOffOutlined from '@mui/icons-material/ToggleOffOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  AuthMethod,
  CompanyShareableType,
  CompliantType,
  deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersId,
  deleteV1CompaniesCompanyIdUsersId,
  getV1CompaniesCompanyIdEquipmentId,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDrivers,
  ModelError_Item,
  ModelError_Response,
  patchV1CompaniesCompanyIdAccountsId,
  patchV1CompaniesCompanyIdCompanySharesId,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { clone, cloneDeep, get, startCase } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { CompliancePanel } from '~components/CompliancePanel/CompliancePanel';
import { complianceStatusColumn } from '~components/CompliancePanel/complianceStatusColumn';
import { CSVImportExportButton } from '~components/CSVImportExport/CSVImportExportButton';
import { CSVImportExportModal } from '~components/CSVImportExport/CSVImportExportModal';
import { useCSVModal } from '~components/CSVImportExport/useCSVModal';
import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import DeactivateConfirmationDialog from '~components/DeactivateConfirmationDialog/DeactivateConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { DriverForm, DriverFormValues } from '~components/DriverForm/DriverForm';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import { InvitesToSendToggleGroup } from '~components/FormFields/v2/ToggleButtonInput';
import LabelList from '~components/Labels/LabelList';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { StatusTabFilters } from '~components/StatusTabFilters/StatusTabFilters';
import UserNotificationsPreferenceForm, {
  UserNotificationsPreferenceDTO,
  UserNotificationsPreferenceHandler,
} from '~components/UserForm/UserNotificationsPreferenceForm';
import {
  SHAREABLE_ROLES,
  UserForm,
  UserFormHandler,
  UserFormStateChangeProps,
  UserValidationDTOWithSitesDiff,
} from '~components/UserForm/v2/UserForm';
import { InviteType } from '~components/UserInvite/UserInviteTypeSelector';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { UserTypesFilter } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { FormStateChangeProps } from '~formsShared/types';
import { useCompany } from '~hooks/useCompany/useCompany';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { useCompanyShares } from '~hooks/useCompanyShares/useCompanyShares';
import { StagedComplianceDoc } from '~hooks/useComplianceDocuments/checklist';
import { useCreateStagedComplianceDocuments } from '~hooks/useComplianceDocuments/useCreateStagedComplianceDocuments';
import useEntityCsvConfig from '~hooks/useCSVImportExport/useEntityCsvConfig';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import { useDriverForm } from '~hooks/useDriverForm/useDriverForm';
import { Driver } from '~hooks/useDrivers/models';
import { useDrivers } from '~hooks/useDrivers/useDrivers';
import {
  BasicEquipment,
  Equipment,
  EquipmentTypeahead,
} from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { Foreman } from '~hooks/useForeman/models';
import { useResourceScopes } from '~hooks/useResourceScopes/useResourceScopes';
import { RoleItem, User } from '~hooks/useUsers/models';
import { useUserRoles } from '~hooks/useUsers/useUserRoles';
import useUsers from '~hooks/useUsers/useUsers';
import {
  EquipmentFormHandlers,
  EquipmentFormValues,
} from '~pages/Settings/Administration/EquipmentManagement/EquipmentForm';
import { CompanyShareFormData } from '~pages/Settings/Administration/EquipmentManagement/EquipmentManagement';
import {
  ExternalEquipmentDTO,
  ExternalEquipmentFormHandlers,
} from '~pages/Settings/Administration/EquipmentManagement/ExternalEquipmentForm';
import connection from '~services/connectionModule';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { dateFormat } from '~utils/dateTime';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import useSingleFlight from '~utils/hooks/useSingleFlight';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { formatPhoneForDisplay } from '~utils/utilFunctions';

import DriverFilters from '../Administration/Drivers/DriverFilters';
import { EquipmentDialog } from '../Administration/EquipmentManagement/components/EquipmentDialog';
import DriverAppShareBanner from './DriverAppShare/DriverAppShareBanner';
import { EquipmentSelector } from './EquipmentSelector';
import UserFilters from './UserFilters';

export interface InvitesToSendState {
  email: { isDisabled: boolean; shouldSend: boolean };
  sms: { isDisabled: boolean; shouldSend: boolean };
}

interface DialogState {
  isOpen: boolean;
  availableAuthMethods: AuthMethod[];
}

export type UserManagementRow = User | Driver | Foreman;

// TODO: move into shared `DataTable` code — https://linear.app/treadapp/issue/TRE-18155
// `DataTable` clips long text, so the full value stays readable on hover.
const textCellWithTitle = (
  getText: (row: UserManagementRow) => string,
  { masked = false }: { masked?: boolean } = {},
) => ({
  renderCell: (row: UserManagementRow) => {
    const text = getText(row);

    if (masked) {
      return (
        <div title={text} role={'presentation'} data-sentry-mask>
          {text}
        </div>
      );
    }

    return <div title={text}>{text}</div>;
  },
  valueGetter: getText,
});

export const UserManagementDataGrid = observer(() => {
  const hideDocumentTypesEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.hideDocumentTypes,
  });

  const location = useLocation();
  const isDriversPage = useMemo(
    () => location.pathname.includes('drivers'),
    [location.pathname],
  );

  const {
    driverDialogRef,
    driverFormIsDirty,
    isDriverDialogOpen,
    setIsDriverDialogOpen,
    driverInviteType,
    setDriverInviteType,
    driverInviteTypeError,
    setDriverInviteTypeError,
    onDriverFormStateChange,
    onDriverInviteTypeChange,
    createNewDriver,
  } = useDriverForm();

  const [userFormDialogState, setUserFormDialogState] = useState<DialogState>({
    isOpen: false,
    availableAuthMethods: [],
  });
  const [userIdToDelete, setUserIdToDelete] = useState<string>();
  const [isEquipmentDialogOpen, setIsEquipmentDialogOpen] = useState(false);
  const [isSharedEquipmentDialogOpen, setIsSharedEquipmentDialogOpen] = useState(false);
  const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const [selectedDriver, setSelectedDriver] = useState<Driver | undefined>();
  const [driverResendSelection, setDriverResendSelection] = useState({
    email: false,
    sms: false,
  });
  const [selectedEquipment, setSelectedEquipment] = useState<
    BasicEquipment | undefined
  >();
  const [userFormIsDirty, setUserFormIsDirty] = useState(false);
  const [
    userNotificationsPreferenceFormIsDirty,
    setUserNotificationsPreferenceFormIsDirty,
  ] = useState(false);

  const [complianceIsDirty, setComplianceIsDirty] = useState(false);
  const [isComplianceEditing, setIsComplianceEditing] = useState(false);
  const formIsDirty =
    userFormIsDirty ||
    driverFormIsDirty ||
    userNotificationsPreferenceFormIsDirty ||
    complianceIsDirty;

  const [invitesToSend, setInvitesToSend] = useState<InvitesToSendState>();
  const [inviteTypes, setInviteTypes] = useState<InviteType[]>([]);
  const [inviteTypeError, setInviteTypeError] = useState<string>();
  const [companyShares, setCompanyShares] = useState<CompanyShare[]>([]);
  const [hasDriverRole, setHasDriverRole] = useState(false);
  const [stagedComplianceDocs, setStagedComplianceDocs] = useState<StagedComplianceDoc[]>(
    [],
  );
  const { userStore, toasterStore, companyAssetsStore } = useStores();
  const showDriverTrailerSettingEnabled =
    userStore.companySettings.isEnabled('show_driver_trailer');
  const {
    getCompanyUsers,
    getCompanyForemen,
    getUserById,
    updateUser,
    createUser,
    sendInvitation,
    isUpdating: isUpdatingUsers,
  } = useUsers();
  const { getUserRoles } = useUserRoles();
  const { getCompanyById } = useCompany();
  const { updateEquipment } = useEquipment();
  const { setSearchQueryValue, searchValue } = useDataGridSearch();
  const {
    createDriverByCompanyId,
    getDriverById,
    getDriversByCompanyId,
    isLoading: isLoadingDrivers,
  } = useDrivers();
  const {
    getCompanySharesByCompanyId,
    createCompanyShareByCompanyId,
    deleteCompanyShare,
    updateCompanyShare,
    isUpdating: isUpdatingCompanyShares,
  } = useCompanyShares();
  const { saveUserSitesDiff } = useResourceScopes();

  const [userTypeFilter, setUserTypeFilter] = useState(UserTypesFilter.USERS);

  const isShowingUsers = userTypeFilter === UserTypesFilter.USERS && !isDriversPage;
  const isShowingForemen = userTypeFilter === UserTypesFilter.FOREMEN && !isDriversPage;
  const isShowingDrivers = !isShowingUsers && !isShowingForemen;
  const companyId = getEffectiveUserCompanyId(userStore);
  const { createStagedDocs } = useCreateStagedComplianceDocuments(companyId);

  const {
    goToPage: goToDriversPage,
    isFetching: isFetchingDrivers,
    pagination: driversPagination,
    reloadCurrentPage: reloadDriversPage,
    rows: driverRows,
    startOver: startDriversOver,
  } = useCursorPagination<Driver>({
    fetchPage: (params) =>
      getDriversByCompanyId({
        companyId,
        filters: companyAssetsStore.driverFilters,
        searchQuery: searchValue,
        // the Users page's internal drivers tab leaves out drivers shared with the company
        shared: isDriversPage ? undefined : false,
        ...params,
      }),
    limit: MAX_API_PAGE_SIZE,
  });
  const {
    goToPage: goToUsersPage,
    isFetching: isFetchingUsers,
    pagination: usersPagination,
    reloadCurrentPage: reloadUsersPage,
    rows: userRows,
    startOver: startUsersOver,
  } = useCursorPagination<User>({
    fetchPage: (params) =>
      getCompanyUsers({
        companyId,
        filters: companyAssetsStore.userFilters,
        searchQuery: searchValue,
        ...params,
      }),
    limit: MAX_API_PAGE_SIZE,
    onFetchError: (error) =>
      reportRequestError(error, t('error_messages.users.failed_to_fetch')),
  });
  const {
    goToPage: goToForemenPage,
    isFetching: isFetchingForemen,
    pagination: foremenPagination,
    reloadCurrentPage: reloadForemenPage,
    rows: foremanRows,
    startOver: startForemenOver,
  } = useCursorPagination<Foreman>({
    fetchPage: (params) =>
      getCompanyForemen({ companyId, searchQuery: searchValue, ...params }),
    limit: MAX_API_PAGE_SIZE,
    onFetchError: (error) =>
      reportRequestError(error, t('error_messages.foremen.failed_to_fetch')),
  });
  const { isInFlight: isSavingDriverEquipment, runSingleFlight } = useSingleFlight();
  // a request still running after a tab switch must not spin the other tabs
  const isGridLoading =
    (isShowingUsers && isFetchingUsers) ||
    (isShowingForemen && isFetchingForemen) ||
    (isShowingDrivers && (isFetchingDrivers || isSavingDriverEquipment));

  const csvLoadedLookups = useMemo(
    () => ({ roles: userStore.userRoles }),
    [userStore.userRoles],
  );
  const { config: csvConfig, loadConfig: loadCsvConfig } = useEntityCsvConfig(
    isDriversPage ? 'drivers' : 'users',
    companyId || '',
    csvLoadedLookups,
  );

  const refreshUsersOrDrivers = useCallback(() => {
    if (isDriversPage) {
      void goToDriversPage();
    } else {
      void goToUsersPage();
    }
  }, [isDriversPage, goToDriversPage, goToUsersPage]);

  const csvModal = useCSVModal({ onImportComplete: refreshUsersOrDrivers });

  useEffect(() => {
    getUserRoles();
  }, []);

  useEffect(() => {
    if (selectedId) {
      getCompanySharesByCompanyId({
        companyId,
        company_shareable_type: CompanyShareableType.USER,
        company_shareable_id: selectedId,
        sender_company_id: companyId,
        callBack: setCompanyShares,
      });
    }
    if (selectedEquipment?.id) {
      getCompanySharesByCompanyId({
        companyId,
        company_shareable_type: CompanyShareableType.EQUIPMENT,
        company_shareable_id: selectedEquipment.id,
        sender_company_id: companyId,
        callBack: setCompanyShares,
      });
    }
  }, [companyId, selectedId, selectedEquipment]);

  const onChangeQuickFilter = (searchQuery: string) => {
    setSearchQueryValue(searchQuery);
  };

  const onChangeInvite = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name as 'email' | 'sms';
    const checked = event.target.checked;

    setInvitesToSend((state) => {
      const newState = cloneDeep(state);

      if (newState) {
        newState[name] = {
          ...newState[name],
          shouldSend: checked,
        };
      }

      return newState;
    });
  }, []);

  const onChangeInviteTypes = useCallback((value: InviteType[]) => {
    setInviteTypes(value);
    setInviteTypeError(undefined);
  }, []);

  const onChangeDriverResend = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const name = event.target.name as 'email' | 'sms';
      const checked = event.target.checked;
      setDriverResendSelection((state) => ({ ...state, [name]: checked }));
    },
    [],
  );

  const updateUserDialogRef = useRef<UserFormHandler>(null);
  const sharedEquipmentDialogRef = useRef<ExternalEquipmentFormHandlers>(null);
  const equipmentDialogRef = useRef<EquipmentFormHandlers>(null);
  const userNotificationsPreferenceRef = useRef<UserNotificationsPreferenceHandler>(null);

  const handleClose = useCallback(() => {
    setUserFormDialogState((state) => ({ ...state, isOpen: false }));
    setIsEquipmentDialogOpen(false);
    setIsSharedEquipmentDialogOpen(false);
    setIsDriverDialogOpen(false);
    setSelectedId(null);
    setSelectedUser(undefined);
    setSelectedDriver(undefined);
    setDriverResendSelection({ email: false, sms: false });
    setSelectedEquipment(undefined);
    setCompanyShares([]);
    setAsyncErrors([]);
    setStagedComplianceDocs([]);
    setHasDriverRole(false);
    setComplianceIsDirty(false);
    setIsComplianceEditing(false);
    setInvitesToSend(undefined);
    setInviteTypes([]);
    setInviteTypeError(undefined);
    setDriverInviteType([]);
    setDriverInviteTypeError(undefined);
  }, []);

  const handleSelectEquipment = (equipment: BasicEquipment, isInternal: boolean) => {
    setSelectedEquipment(equipment);
    if (isInternal) {
      setIsEquipmentDialogOpen(true);
    } else {
      setIsSharedEquipmentDialogOpen(true);
    }
  };

  const handleErrors = (error: AxiosError<ModelError_Response>) => {
    setAsyncErrors(error.response?.data.error.errors || []);
  };

  const handleDeleteCompanyShares = (
    originalCompanyShares: CompanyShare[],
    companyShares: CompanyShareFormData[],
  ) => {
    const originalCompanySharesExcludingCurrentCompany = originalCompanyShares.filter(
      (companyShare) => companyShare.receiverCompany.id !== companyId,
    );

    const deletedCompanyShares = originalCompanySharesExcludingCurrentCompany.filter(
      (originalCompanyShare) =>
        !companyShares.some(
          (companyShare: { id: string | undefined }) =>
            companyShare.id === originalCompanyShare.id,
        ),
    );

    if (deletedCompanyShares?.length) {
      deletedCompanyShares.forEach((companyShare) => {
        deleteCompanyShare(companyShare.id);
      });
    }
  };

  const handleUpdateCompanyShare = async (data: {
    externalId?: string;
    ticketMatchId?: string;
  }) => {
    try {
      await updateCompanyShare({
        companyShareId: selectedEquipment?.companyShare?.id ?? '',
        external_id: data.externalId || '',
        ticket_match_id: data.ticketMatchId || '',
      });
    } catch (error) {
      handleErrors(error as AxiosError<ModelError_Response>);
      // We need to throw the error to prevent the onSuccess function from being called in the onSubmitCallback
      throw error;
    }
  };
  const handleDeleteCompanyShare = useCallback(
    async (companyShareId: string) => {
      try {
        await deleteCompanyShare(companyShareId);
        toasterStore.push(
          alert(t('administration.drivers.share_removed'), AlertTypes.success),
        );
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.company_shares.failed_to_unshare'),
        );

        return;
      }

      if (isShowingForemen) {
        await reloadForemenPage();
      } else {
        await reloadDriversPage();
      }
    },
    [deleteCompanyShare, reloadDriversPage, isShowingForemen, reloadForemenPage],
  );

  const handleDeleteUser = useCallback(async () => {
    if (!userIdToDelete) {
      return Promise.resolve();
    }

    try {
      const managedDriver = driverRows.find(
        (driver) => driver.id === userIdToDelete && driver.managed,
      );

      if (managedDriver) {
        await deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersId({
          path: {
            'company-id': companyId,
            'managed-company-id': managedDriver.company.id,
            id: userIdToDelete,
          },
        });
      } else {
        await deleteV1CompaniesCompanyIdUsersId({
          path: { 'company-id': companyId, id: userIdToDelete },
        });
      }

      const message =
        isShowingUsers || isShowingForemen
          ? t('administration.users.user_deactivated')
          : t('administration.drivers.driver_deactivated');

      toasterStore.push(alert(message, AlertTypes.success));
    } catch (error) {
      const message =
        isShowingUsers || isShowingForemen
          ? t('error_messages.users.failed_to_deactivate')
          : t('error_messages.drivers.failed_to_deactivate');

      connection.handleRequestError(error, message);

      return;
    }

    setUserIdToDelete(undefined);

    if (isShowingUsers) {
      await reloadUsersPage();
    } else if (isShowingForemen) {
      await reloadForemenPage();
    } else {
      await reloadDriversPage();
    }
  }, [
    userIdToDelete,
    companyId,
    driverRows,
    isShowingUsers,
    isShowingForemen,
    reloadDriversPage,
    reloadUsersPage,
    reloadForemenPage,
  ]);

  const [isUpdatingDriverAccount, setIsUpdatingDriverAccount] = useState(false);
  const isUpdating = isUpdatingUsers || isUpdatingDriverAccount;
  const updateDriverAccount = useCallback(
    async (accountName?: string, accountExternalId?: string) => {
      if (!selectedDriver || !selectedDriver.companyShare?.receiverAccount) {
        return;
      }

      try {
        setIsUpdatingDriverAccount(true);
        await patchV1CompaniesCompanyIdAccountsId({
          path: {
            'company-id': companyId,
            id: selectedDriver.companyShare.receiverAccount.id,
          },
          body: { name: accountName, external_id: accountExternalId },
        });
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.account.failed_to_update'),
        );
      } finally {
        setIsUpdatingDriverAccount(false);
      }
    },
    [selectedDriver, companyId],
  );

  const editRow = async (id: string) => {
    setSelectedId(id);
    let fetchedUser: User | Driver | undefined;

    if (isDriversPage) {
      fetchedUser = await getDriverById(id);
      setSelectedDriver(fetchedUser as Driver);
      setUserFormDialogState({ isOpen: true, availableAuthMethods: [] });
    } else {
      fetchedUser = await getUserById(id);
      setSelectedUser(fetchedUser as User);

      if (fetchedUser?.company?.id) {
        const company = await getCompanyById({ id: String(fetchedUser?.company?.id) });

        if (company) {
          setUserFormDialogState({
            isOpen: true,
            availableAuthMethods: company.authMethods,
          });

          if ((fetchedUser as User)?.isActiveMember) {
            setInvitesToSend(undefined);
          } else {
            setInvitesToSend({
              email: { isDisabled: true, shouldSend: false },
              sms: { isDisabled: true, shouldSend: false },
            });
          }
        }
      }
    }
  };

  const createNewUser = async () => {
    setSelectedId(null);

    const company = await getCompanyById({ id: String(companyId) });

    if (company) {
      setUserFormDialogState({ isOpen: true, availableAuthMethods: company.authMethods });
      setInviteTypes([]);
      setInviteTypeError(undefined);
    }
  };

  const resendInvite = async (
    rowId: string,
    method: 'email' | 'sms',
    managedCompanyId?: string,
    // The Edit Driver dialog can resend over both channels in one click, so it
    // suppresses the per-call toast and pushes a single confirmation itself.
    options?: { silent?: boolean },
  ) => {
    if (rowId) {
      try {
        await sendInvitation(
          rowId,
          method,
          managedCompanyId ? companyId : undefined,
          managedCompanyId,
        );
        if (!options?.silent) {
          toasterStore.push(alert(t('user.invitation_sent'), AlertTypes.success));
        }

        return true;
      } catch {
        // Error toast is handled in the hook.
        return false;
      }
    }

    return false;
  };

  // Drives the "Resend invitation" section on the Edit Driver dialog. Resend is
  // offered for a saved, pending driver this company owns — directly or as a
  // managed owner-operator. A shared driver's invite belongs to the sender
  // company, so it is excluded.
  const driverResendInvite = useMemo(() => {
    if (!selectedId || !selectedDriver || selectedDriver.companyShare) {
      return null;
    }

    const canResendEmail =
      !selectedDriver.isActiveMember && Boolean(selectedDriver.email);
    const canResendSms = !selectedDriver.isActiveUser && Boolean(selectedDriver.phone);

    if (!canResendEmail && !canResendSms) {
      return null;
    }

    return {
      id: selectedDriver.id,
      canResendEmail,
      canResendSms,
      managedCompanyId: selectedDriver.managed ? selectedDriver.company.id : undefined,
    };
  }, [selectedId, selectedDriver]);

  const handleDriverResend = async () => {
    if (!driverResendInvite) {
      return;
    }

    const { id, managedCompanyId } = driverResendInvite;

    let anyInviteSent = false;

    if (driverResendSelection.email) {
      const emailSent = await resendInvite(id, 'email', managedCompanyId, {
        silent: true,
      });
      anyInviteSent = anyInviteSent || emailSent;
    }

    if (driverResendSelection.sms) {
      const smsSent = await resendInvite(id, 'sms', managedCompanyId, { silent: true });
      anyInviteSent = anyInviteSent || smsSent;
    }

    // A single "Send now" click yields one confirmation even when both
    // channels were selected. Failed channels surface their own error toast.
    if (anyInviteSent) {
      toasterStore.push(alert(t('user.invitation_sent'), AlertTypes.success));
    }

    setDriverResendSelection({ email: false, sms: false });
  };

  const onSuccess = (user?: User) => {
    handleClose();
    const isEditing = Boolean(selectedId);
    const isEditingDriver = isShowingDrivers && isEditing;
    if (isShowingUsers && !isEditing) {
      void goToUsersPage();
    } else if (isShowingUsers) {
      void reloadUsersPage();
    } else if (isShowingForemen && !isEditing) {
      void goToForemenPage();
    } else if (isShowingForemen) {
      void reloadForemenPage();
    } else if (isEditingDriver) {
      void reloadDriversPage();
    } else if (isShowingDrivers) {
      void goToDriversPage();
    }
    const name = `${user?.firstName} ${user?.lastName}`;
    toasterStore.push(
      alert(
        selectedId ? t('user.user_updated', { name }) : t('user.user_created', { name }),
        AlertTypes.success,
      ),
    );
  };

  const onSharedEquipmentSubmitCallback = (data: ExternalEquipmentDTO) => {
    const originalCompanyShares = companyShares;
    const submittedCompanyShares = data.companyShares as CompanyShareFormData[];

    handleDeleteCompanyShares(originalCompanyShares, submittedCompanyShares);

    const promises = [];

    if (submittedCompanyShares.length) {
      submittedCompanyShares.forEach((companyShare) => {
        if (companyShare.id) {
          // Update existing company share
          promises.push(
            updateCompanyShare({
              companyShareId: companyShare.id,
              dispatchable: companyShare.dispatchable.id === 'dispatchable',
              ticket_match_id: companyShare.ticketMatchId,
            }),
          );
        } else {
          // Create new company share
          promises.push(
            createCompanyShareByCompanyId({
              companyId: companyId,
              company_shareable_id: selectedEquipment?.id as string,
              company_shareable_type: 'Equipment' as CompanyShareableType,
              receiver_company_id: companyShare.receiverCompany.id,
              dispatchable: companyShare.dispatchable.id === 'dispatchable',
              ticket_match_id: companyShare.ticketMatchId,
            }),
          );
        }
      });
    }

    promises.push(
      handleUpdateCompanyShare({
        externalId: data.externalId,
        ticketMatchId: data.ticketMatchId,
      }),
    );

    Promise.all(promises)
      .then(() => {
        // We don't update the driver object directly when we update a company share
        // Fetch the drvers in this instance to show the most recent data in the table
        refreshCurrentTab();

        onEquipmentUpdateSuccess(data);
      })
      .catch((error) => {
        console.error('Error occurred during the operation:', error);
      });
  };

  const onEquipmentUpdateSuccess = (
    data: ExternalEquipmentDTO | EquipmentFormValues | Equipment,
  ) => {
    const id = get(data, 'id');

    if (!toasterStore.alerts.length && id) {
      getV1CompaniesCompanyIdEquipmentId({
        path: { 'company-id': companyId, id: id },
      }).then((response) => {
        const equipment = Equipment.parse(response.data.data);
        const messageArgs = { name: equipment.name, equipment_id: equipment.equipmentId };

        toasterStore.push(
          alert(
            selectedId
              ? t('administration.equipment.equipment_updated', messageArgs)
              : t('administration.equipment.equipment_created', messageArgs),
            AlertTypes.success,
          ),
        );

        // Only close the modal if the action is successful
        handleClose();
      });
    }
  };

  const onSubmitCallback = async (
    data: UserValidationDTOWithSitesDiff,
    userNotificationsPreferenceData?: UserNotificationsPreferenceDTO,
  ) => {
    const userCompanyId =
      (data.company as { id?: string } | null | undefined)?.id || companyId;
    const isCreating = !selectedId;
    const shouldSendEmailInvite = isCreating
      ? inviteTypes.includes('email')
      : Boolean(!invitesToSend?.email.isDisabled && invitesToSend?.email.shouldSend);
    const shouldSendSmsInvite = isCreating
      ? inviteTypes.includes('sms')
      : Boolean(!invitesToSend?.sms.isDisabled && invitesToSend?.sms.shouldSend);

    setAsyncErrors([]);

    if (selectedId) {
      try {
        // For managed drivers, pass the driver's company ID as managedCompanyId
        // so the backend can find the user with User.managed scope
        const managedCompanyId = selectedDriver?.managed
          ? selectedDriver.company?.id
          : undefined;
        const scopedCompanyId = managedCompanyId ? companyId : userCompanyId;

        if (!selectedDriver || !selectedDriver.companyShare || selectedDriver.managed) {
          const isSharedDriver = Boolean(selectedDriver?.companyShare?.id);
          const { additionalEquipment: _discardedAE, ...dataWithoutAE } = data;

          await updateUser(
            {
              id: selectedId,
              ...(isSharedDriver ? dataWithoutAE : data),
            } as unknown as User,
            userNotificationsPreferenceData,
            scopedCompanyId,
            managedCompanyId,
          );
        }
        const companySharesPresentOnModalOpen = companyShares;
        const companySharesPresentOnSubmit = data.companyShares?.filter(
          (submittedCompanyShare) => !!submittedCompanyShare.receiverCompany.id,
        );

        // Generates a list of company shares created this session that we need to create
        const companySharesToCreate = companySharesPresentOnSubmit?.filter(
          (submittedCompanyShare) =>
            !companySharesPresentOnModalOpen.some(
              (existingCompanyShare) =>
                existingCompanyShare.receiverCompany.id ===
                submittedCompanyShare.receiverCompany.id,
            ),
        );

        const companySharesToUpdate = companySharesPresentOnSubmit?.filter(
          (submittedCompanyShare) => submittedCompanyShare.id,
        );

        const updatedUserHasShareableRole = ((data.userRoles ?? []) as RoleItem[]).some(
          (item) => SHAREABLE_ROLES.includes(item.name.toLowerCase()),
        );

        if (updatedUserHasShareableRole || isDriversPage) {
          // Generates a list of company shares that were removed this session that we need to delete
          const companySharesToDelete = companySharesPresentOnModalOpen.filter(
            (existingCompanyShare) =>
              companySharesPresentOnSubmit &&
              !companySharesPresentOnSubmit.some(
                (submittedCompanyShare) =>
                  existingCompanyShare.receiverCompany.id ===
                  submittedCompanyShare.receiverCompany.id,
              ),
          );

          if (companySharesToCreate && companySharesToCreate.length) {
            companySharesToCreate.forEach((companyShare) => {
              createCompanyShareByCompanyId({
                companyId,
                company_shareable_id: selectedId,
                receiver_company_id: companyShare.receiverCompany.id as string,
                company_shareable_type: 'User' as CompanyShareableType,
                rate_override: companyShare.rateOverride ?? false,
              });
            });
          }

          if (companySharesToUpdate && companySharesToUpdate.length) {
            companySharesToUpdate.forEach(async (companyShare) => {
              await updateCompanyShare({
                companyShareId: companyShare.id as string,
                rate_override: companyShare.rateOverride ?? false,
              });
            });
          }

          if (companySharesToDelete && companySharesToDelete.length) {
            companySharesToDelete.forEach((companyShare) => {
              deleteCompanyShare(companyShare.id as string);
            });
          }

          if (selectedDriver?.companyShare) {
            await updateCompanyShare({
              companyShareId: selectedDriver.companyShare!.id,
              default_equipment_id: data.defaultDriverEquipment?.id ?? null,
            });

            if (showDriverTrailerSettingEnabled) {
              await patchV1CompaniesCompanyIdCompanySharesId({
                path: { 'company-id': companyId, id: selectedDriver.companyShare!.id },
                body: {
                  default_additional_equipment_ids: data.defaultDriverAdditionalEquipment
                    ?.id
                    ? [data.defaultDriverAdditionalEquipment.id]
                    : [],
                },
              });
            }
          }

          if (selectedDriver?.companyShare?.receiverAccount) {
            updateDriverAccount(data.accountName, data.accountExternalId);
          }
        }

        if (shouldSendEmailInvite) {
          await sendInvitation(selectedId, 'email', scopedCompanyId, managedCompanyId);
        }

        if (shouldSendSmsInvite) {
          await sendInvitation(selectedId, 'sms', scopedCompanyId, managedCompanyId);
        }

        await saveUserSitesDiff(
          selectedId,
          data.sitesDiff,
          scopedCompanyId,
          managedCompanyId,
        );

        onSuccess({ id: selectedId, ...data } as unknown as User);
      } catch (error) {
        const e = error as AxiosError<ModelError_Response>;

        setAsyncErrors(e.response?.data.error.errors || []);
      }
    } else {
      try {
        const newUser = await createUser(data as unknown as User, {
          email: shouldSendEmailInvite,
          sms: shouldSendSmsInvite,
        });
        const submittedCompanyShares = data.companyShares;

        if (newUser && submittedCompanyShares && submittedCompanyShares.length) {
          submittedCompanyShares.forEach((companyShare) => {
            createCompanyShareByCompanyId({
              companyId: userCompanyId,
              receiver_company_id: companyShare.receiverCompany.id as string,
              company_shareable_id: newUser.id as string,
              company_shareable_type: 'User' as CompanyShareableType,
              rate_override: companyShare.rateOverride ?? false,
            });
          });
        }

        await saveUserSitesDiff(newUser.id, data.sitesDiff, userCompanyId);

        // Create staged compliance documents for the new user
        const { failed } = await createStagedDocs(
          CompliantType.USER,
          newUser.id,
          stagedComplianceDocs,
        );
        if (failed > 0) {
          toasterStore.push(
            alert(
              t('error_messages.compliance_document_types.partial_save_failure'),
              AlertTypes.warn,
            ),
          );
        }

        onSuccess(data as unknown as User);
      } catch (error) {
        const e = error as AxiosError<ModelError_Response>;

        setAsyncErrors(e.response?.data.error.errors || []);
      }
    }
  };

  const onSubmitForm = async () => {
    const isInviteTypeMissing =
      !selectedId && !selectedDriver && inviteTypes.length === 0;
    setInviteTypeError(isInviteTypeMissing ? t('invite_type.required') : undefined);

    const userData = await new Promise<UserValidationDTOWithSitesDiff | undefined>(
      (resolve) => {
        updateUserDialogRef.current?.submit((data) => resolve(data));
      },
    );

    if (isInviteTypeMissing) {
      return;
    }

    const userNotificationsPreferenceData = await new Promise<
      UserNotificationsPreferenceDTO | undefined
    >((resolve) => {
      if (selectedUser?.id && userNotificationsPreferenceRef.current) {
        userNotificationsPreferenceRef.current?.onSubmit?.((data) => resolve(data));
      } else {
        resolve(undefined);
      }
    });

    if (userData) {
      await onSubmitCallback(userData, userNotificationsPreferenceData);
    }
  };

  const onSubmitSharedEquipmentForm = async () => {
    const sharedEquipmentData = await new Promise<ExternalEquipmentDTO | undefined>(
      (resolve) => {
        sharedEquipmentDialogRef.current?.submit((data) => resolve(data));
      },
    );

    if (sharedEquipmentData) {
      await onSharedEquipmentSubmitCallback(sharedEquipmentData);
    }
  };

  const onSubmitEquipmentForm = () => {
    equipmentDialogRef.current?.submit(onEquipmentSubmitCallback);
  };

  const onEquipmentSubmitCallback = (
    data: EquipmentFormValues & {
      companySpecificId?: string;
    },
  ) => {
    const originalCompanyShares = companyShares;
    const submittedCompanyShares = (data.companyShares ?? []) as CompanyShareFormData[];
    const promises = [];

    // Handle existing company shares deletion
    handleDeleteCompanyShares(originalCompanyShares, submittedCompanyShares);

    // Handle company shares updates and creation
    if (submittedCompanyShares?.length) {
      submittedCompanyShares.forEach((companyShare) => {
        if (companyShare.id) {
          // Update existing company share
          promises.push(
            updateCompanyShare({
              companyShareId: companyShare.id,
              dispatchable: companyShare.dispatchable.id === 'dispatchable',
              ticket_match_id: companyShare.ticketMatchId,
            }),
          );
        } else {
          // Create new company share
          promises.push(
            createCompanyShareByCompanyId({
              companyId: companyId,
              company_shareable_id: companyShare.companyShareableId,
              company_shareable_type: 'Equipment' as CompanyShareableType,
              receiver_company_id: companyShare.receiverCompany.id,
              dispatchable: companyShare.dispatchable.id === 'dispatchable',
              ticket_match_id: companyShare.ticketMatchId,
            }),
          );
        }
      });
    }

    if (selectedEquipment?.companyShare?.id && data.companySpecificId) {
      promises.push(
        updateCompanyShare({
          companyShareId: selectedEquipment.companyShare.id,
          external_id: data.companySpecificId,
        }),
      );
    } else {
      promises.push(updateEquipment({ equipment: data as unknown as Equipment }));
    }

    Promise.all(promises)
      .then(() => {
        refreshCurrentTab();
        onEquipmentUpdateSuccess(data);
      })
      .catch((error) => {
        handleErrors(error as AxiosError<ModelError_Response>);
      });
  };

  const onDriverSubmitCallback = async (data: DriverFormValues) => {
    const shouldSendEmailInvite = driverInviteType.includes('email');
    const shouldSendSmsInvite = driverInviteType.includes('sms');
    const invitesToSend = { email: shouldSendEmailInvite, sms: shouldSendSmsInvite };

    try {
      const newAccount = await createDriverByCompanyId(data, invitesToSend);

      if (newAccount?.id && stagedComplianceDocs.length > 0) {
        // The invite API creates an owner-operator Company with a sole
        // driver. Fetch that driver via the managed-company endpoint to
        // get the User ID for compliance document association. This
        // avoids the search index, which is populated asynchronously
        // and would not be available immediately after creation.
        try {
          const managedCompanyId = newAccount.connectedCompany?.id;
          if (managedCompanyId) {
            const response =
              await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDrivers({
                path: { 'company-id': companyId, 'managed-company-id': managedCompanyId },
                query: { 'page[limit]': 1 },
              });
            const drivers = response.data.data as { id: string }[];
            const driverId = drivers?.[0]?.id;

            if (driverId) {
              const { failed } = await createStagedDocs(
                CompliantType.USER,
                driverId,
                stagedComplianceDocs,
              );
              if (failed > 0) {
                toasterStore.push(
                  alert(
                    t('error_messages.compliance_document_types.partial_save_failure'),
                    AlertTypes.warn,
                  ),
                );
              }
            }
          }
        } catch (error) {
          console.error('Failed to attach compliance documents', error);
          toasterStore.push(
            alert(
              t('error_messages.compliance_document_types.partial_save_failure'),
              AlertTypes.warn,
            ),
          );
        }
      }

      setStagedComplianceDocs([]);
      onSuccess(data as unknown as User);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        const e = error as AxiosError<ModelError_Response>;

        setAsyncErrors(e.response?.data.error.errors || []);
      } else {
        connection.handleRequestError(
          error,
          t('error_messages.drivers.failed_to_create_driver'),
        );
      }
    }
  };

  const onSubmitDriverForm = async () => {
    const isInviteTypeMissing = driverInviteType.length === 0;
    setDriverInviteTypeError(isInviteTypeMissing ? t('invite_type.required') : undefined);

    const driverData = await new Promise<DriverFormValues | undefined>((resolve) => {
      driverDialogRef.current?.submit((data) => resolve(data));
    });

    if (isInviteTypeMissing) {
      return;
    }

    if (driverData) {
      await onDriverSubmitCallback(driverData);
    }
  };

  const onFormStateChange = ({
    isDirty,
    hasValidEmail,
    hasValidPhone,
    hasDriverRole,
  }: UserFormStateChangeProps) => {
    setUserFormIsDirty(isDirty);
    setHasDriverRole(hasDriverRole ?? false);

    if (invitesToSend) {
      setInvitesToSend((state) => {
        const newState = cloneDeep(state);

        if (newState) {
          newState.email = {
            ...newState.email,
            isDisabled: !hasValidEmail,
          };

          newState.sms = {
            ...newState.sms,
            isDisabled: !hasValidPhone,
          };
        }

        return newState;
      });
    }
  };

  const onUserNotificationsPreferenceFormStateChange = useCallback(
    ({ isDirty }: FormStateChangeProps) => {
      setUserNotificationsPreferenceFormIsDirty(isDirty);
    },
    [],
  );

  useEffect(() => {
    if (!hasDriverRole && !selectedDriver) {
      setStagedComplianceDocs([]);
    }
  }, [hasDriverRole, selectedDriver]);

  const rows = useMemo<UserManagementRow[]>(() => {
    if (isShowingUsers) {
      return userRows;
    }

    if (isShowingForemen) {
      return foremanRows;
    }

    return driverRows;
  }, [userRows, foremanRows, driverRows, isShowingUsers, isShowingForemen]);

  const userPermissions = useMemo(() => {
    return userStore.getPermissions();
  }, [userStore.getPermissions()]);

  const saveDriverEquipment = useCallback(
    async (save: () => Promise<unknown>) => {
      const isSaved = await runSingleFlight(async () => {
        try {
          await save();
        } catch {
          // Each save reports its own failure.
          return false;
        }

        await reloadDriversPage();

        return true;
      });

      return isSaved === true;
    },
    [reloadDriversPage, runSingleFlight],
  );

  const columns = useMemo<Array<DataTableColumn<UserManagementRow>>>(() => {
    const updateDriver = async (
      driver: Driver,
      equipmentChanges: Partial<Pick<Driver, 'additionalEquipment' | 'equipment'>>,
    ) => {
      const driverToSave = Object.assign(clone(driver), equipmentChanges);

      try {
        // `User.deparse` reads only fields a `Driver` also carries.
        await updateUser(
          driverToSave as unknown as User,
          undefined,
          driver.managed ? companyId : undefined,
          driver.managed ? driver.company.id : undefined,
        );
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          reportRequestError(error, t('error_messages.users.failed_to_update'));
        }

        throw error;
      }
    };

    return [
      {
        field: 'lastName',
        headerName: t('form_fields.last_name'),
        ...textCellWithTitle((row) => row.lastName, { masked: true }),
      },
      {
        field: 'firstName',
        headerName: t('form_fields.first_name'),
        ...textCellWithTitle((row) => row.firstName, { masked: true }),
      },
      ...(isShowingUsers
        ? []
        : [
            {
              field: 'senderCompany',
              headerName: t('form_fields.sending_company'),
              ...textCellWithTitle((row) => {
                if (row instanceof User) {
                  return '';
                }
                const senderCompanyName =
                  row.companyShare?.senderCompany?.legalName || '';
                if (!senderCompanyName) {
                  return '';
                }
                const driverFullName = `${row.firstName} ${row.lastName}`;
                if (senderCompanyName.trim() === driverFullName.trim()) {
                  return '';
                }
                return senderCompanyName;
              }),
            },
          ]),
      ...(isShowingUsers || isShowingForemen
        ? []
        : [
            {
              field: 'companyType',
              headerName: t('form_fields.type'),
              ...textCellWithTitle((row) =>
                startCase(row.company?.companyType || 'User'),
              ),
            },
          ]),
      ...(isShowingUsers
        ? [
            {
              field: 'company',
              headerName: t('form_fields.company'),
              valueGetter: (row: UserManagementRow) => row.company?.legalName || '',
            },
          ]
        : []),
      ...(isShowingForemen
        ? []
        : [
            {
              field: 'roles',
              headerName: isShowingUsers
                ? t('form_fields.role')
                : t('form_fields.equipment'),
              renderCell: (row: UserManagementRow) => {
                if (isShowingUsers) {
                  if (!(row instanceof User)) {
                    return null;
                  }

                  return (
                    <Typography variant="body2">
                      {row.userRoles?.map((role) => role.name).join(', ')}
                    </Typography>
                  );
                }

                if (!(row instanceof Driver)) {
                  return null;
                }

                const driver = row;
                const isInternalDriver = driver.company.id === companyId;
                const noDefaultEquipment =
                  !driver.equipment && !driver.companyShare?.defaultEquipment;

                return (
                  <Box
                    sx={{
                      display: 'flex' as const,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        flex: 3,
                        minWidth: 200,
                      }}
                    >
                      {isInternalDriver ? (
                        <EquipmentSelector
                          defaultEquipment={driver.equipment || null}
                          disabled={!userPermissions.canEditUser || isUpdating}
                          onSelect={(equipment: Nullable<EquipmentTypeahead>) =>
                            saveDriverEquipment(() =>
                              updateDriver(driver, {
                                equipment: equipment as BasicEquipment,
                              }),
                            )
                          }
                          clearable={false}
                        />
                      ) : (
                        <>
                          {driver.companyShare && (
                            <EquipmentSelector
                              defaultEquipment={
                                driver.companyShare.defaultEquipment || driver.equipment
                              }
                              disabled={!userPermissions.canEditUser || isUpdating}
                              onSelect={(equipment: Nullable<EquipmentTypeahead>) => {
                                if (equipment) {
                                  return saveDriverEquipment(() =>
                                    updateCompanyShare({
                                      companyShareId: driver.companyShare!.id,
                                      default_equipment_id: equipment.id,
                                    }),
                                  );
                                }
                              }}
                              clearable={false}
                            />
                          )}
                        </>
                      )}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 100,
                      }}
                    >
                      {((driver.companyShare && driver.companyShare.defaultEquipment) ||
                        (!driver.companyShare && driver.equipment)) && (
                        <>
                          <Tooltip title={t('administration.equipment.edit_truck')}>
                            <IconButton
                              disabled={noDefaultEquipment}
                              onClick={() => {
                                // the equipment to edit is either driver's equipment or external equipment
                                const equipmentToEdit = driver.companyShare
                                  ?.defaultEquipment
                                  ? (driver.companyShare
                                      ?.defaultEquipment as unknown as BasicEquipment)
                                  : (driver.equipment as unknown as BasicEquipment);
                                // whether the equipment is external or internal is determined by if it has the companyShare property
                                const isEquipmentExternal =
                                  !!equipmentToEdit.companyShare;

                                handleSelectEquipment(
                                  equipmentToEdit,
                                  !isEquipmentExternal,
                                );
                              }}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('administration.equipment.clear_truck')}>
                            <IconButton
                              disabled={noDefaultEquipment}
                              onClick={() => {
                                const { companyShare } = driver;

                                if (companyShare) {
                                  void saveDriverEquipment(async () => {
                                    await updateCompanyShare({
                                      companyShareId: companyShare.id,
                                      default_equipment_id: null,
                                    });

                                    if (driver.managed) {
                                      await updateDriver(driver, { equipment: null });
                                    }
                                  });
                                } else if (driver.equipment) {
                                  void saveDriverEquipment(() =>
                                    updateDriver(driver, { equipment: null }),
                                  );
                                }
                              }}
                            >
                              <Clear />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Box>
                );
              },
            },
          ]),
      ...(isShowingForemen || isShowingUsers || !showDriverTrailerSettingEnabled
        ? []
        : [
            {
              field: 'additionalEquipment',
              headerName: t('form_fields.trailer'),
              renderCell: (row: UserManagementRow) => {
                if (!(row instanceof Driver)) {
                  return null;
                }

                const driver = row;
                const isInternalDriver = driver.company.id === companyId;

                const trailerEquipment = isInternalDriver
                  ? driver.additionalEquipment?.[0] || null
                  : driver.companyShare?.defaultAdditionalEquipment?.[0] || null;

                return (
                  <Box
                    sx={{
                      display: 'flex' as const,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        flex: 3,
                        minWidth: 200,
                      }}
                    >
                      {isInternalDriver ? (
                        <EquipmentSelector
                          defaultEquipment={trailerEquipment}
                          disabled={!userPermissions.canEditUser || isUpdating}
                          onSelect={(equipment: Nullable<EquipmentTypeahead>) => {
                            if (equipment) {
                              return saveDriverEquipment(() =>
                                updateDriver(driver, {
                                  additionalEquipment: [equipment as BasicEquipment],
                                }),
                              );
                            }
                          }}
                          clearable={false}
                          placeholder={t('form_fields.select_trailer')}
                        />
                      ) : (
                        <>
                          {driver.companyShare && (
                            <EquipmentSelector
                              defaultEquipment={trailerEquipment}
                              disabled={!userPermissions.canEditUser || isUpdating}
                              onSelect={(equipment: Nullable<EquipmentTypeahead>) => {
                                if (equipment) {
                                  return saveDriverEquipment(() =>
                                    updateCompanyShare({
                                      companyShareId: driver.companyShare!.id,
                                      default_additional_equipment_ids: [equipment.id],
                                    }),
                                  );
                                }
                              }}
                              clearable={false}
                              placeholder={t('form_fields.select_trailer')}
                            />
                          )}
                        </>
                      )}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 100,
                      }}
                    >
                      {trailerEquipment && (
                        <>
                          <Tooltip title={t('administration.equipment.edit_trailer')}>
                            <IconButton
                              disabled={!trailerEquipment}
                              onClick={() => {
                                const equipmentToEdit =
                                  trailerEquipment as unknown as BasicEquipment;
                                const isEquipmentExternal =
                                  !!equipmentToEdit.companyShare;

                                handleSelectEquipment(
                                  equipmentToEdit,
                                  !isEquipmentExternal,
                                );
                              }}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('administration.equipment.clear_trailer')}>
                            <IconButton
                              disabled={!trailerEquipment}
                              onClick={() => {
                                const { companyShare } = driver;

                                if (companyShare) {
                                  void saveDriverEquipment(() =>
                                    updateCompanyShare({
                                      companyShareId: companyShare.id,
                                      default_additional_equipment_ids: [],
                                    }),
                                  );
                                } else {
                                  void saveDriverEquipment(() =>
                                    updateDriver(driver, { additionalEquipment: [] }),
                                  );
                                }
                              }}
                            >
                              <Clear />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Box>
                );
              },
            },
          ]),
      {
        field: 'externalId',
        headerName: t('form_fields.external_id'),
        ...textCellWithTitle((row) => row.externalId || ''),
      },
      {
        defaultHidden: true,
        field: 'accountingId',
        headerName: t('form_fields.accounting_id'),
        ...textCellWithTitle((row) =>
          row instanceof User ? row.accountingId || '' : '',
        ),
      },
      {
        field: 'email',
        headerName: t('form_fields.email'),
        ...textCellWithTitle((row) => row.email ?? '', { masked: true }),
      },
      {
        field: 'phone',
        headerName: t('form_fields.phone'),
        ...textCellWithTitle((row) => formatPhoneForDisplay(row.phone), { masked: true }),
      },
      {
        field: 'timeZone',
        headerName: t('form_fields.time_zone'),
        ...textCellWithTitle((row) => row.timeZone || ''),
      },
      ...(!isShowingForemen && !hideDocumentTypesEnabled
        ? [complianceStatusColumn<UserManagementRow>()]
        : []),
      {
        field: 'labels',
        headerName: t('form_fields.labels'),
        renderCell: (row) => <LabelList labels={row.labels} />,
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        renderCell: (row) => {
          const { createdAt, updatedAt } =
            row instanceof Foreman ? { createdAt: null, updatedAt: null } : row;

          return (
            <Tooltip
              title={`${t('common.last_update')}: ${updatedAt ? dateFormat(updatedAt) : ''}`}
            >
              <Typography variant="body2">
                {createdAt ? dateFormat(createdAt) : ''}
              </Typography>
            </Tooltip>
          );
        },
      },
      actionsColumn((row) => {
        const options = [];
        const companyShare = row instanceof User ? null : row.companyShare;
        const isSharedForeman =
          isShowingForemen && row instanceof Foreman && Boolean(companyShare?.id);
        // Foremen carry no activation status, so their invites stay resendable.
        const { isActiveMember, isActiveUser } =
          row instanceof Foreman ? { isActiveMember: false, isActiveUser: false } : row;

        if (!isDriversPage && !isSharedForeman && !isActiveMember && row.email) {
          options.push({
            title: t('user.resend_email_invitation'),
            icon: <Mail />,
            callBack: () => resendInvite(row.id, 'email'),
          });
        }

        if (!isDriversPage && !isSharedForeman && !isActiveUser && row.phone) {
          options.push({
            title: t('user.resend_sms_invitation'),
            icon: <Sms />,
            callBack: () => resendInvite(row.id, 'sms'),
          });
        }

        if (isDriversPage && row instanceof Driver) {
          // Resend applies to drivers this company owns — either directly
          // (own-company) or as a managed owner-operator. A shared driver
          // (companyShare present) belongs to the sender company, whose
          // invite this company has no authority to resend.
          const isSharedDriver = Boolean(companyShare);
          const managedCompanyId = row.managed ? row.company.id : undefined;

          if (!isSharedDriver && !isActiveMember && row.email) {
            options.push({
              title: t('user.resend_email_invitation'),
              icon: <Mail />,
              callBack: () => resendInvite(row.id, 'email', managedCompanyId),
            });
          }

          if (!isSharedDriver && !isActiveUser && row.phone) {
            options.push({
              title: t('user.resend_sms_invitation'),
              icon: <Sms />,
              callBack: () => resendInvite(row.id, 'sms', managedCompanyId),
            });
          }
        }

        if (userPermissions.canDeleteUser) {
          options.push({
            title: companyShare ? t('actions.remove_share') : t('actions.deactivate'),
            icon: <ToggleOffOutlined />,
            callBack: () => {
              if (companyShare?.id) {
                handleDeleteCompanyShare(companyShare.id);
              } else {
                setUserIdToDelete(row.id);
              }
            },
          });
        }

        return (
          <>
            {userPermissions.canEditUser && !isSharedForeman && (
              <IconButton
                aria-label={t('actions.edit')}
                size="small"
                onClick={() => editRow(row.id)}
              >
                <Edit />
              </IconButton>
            )}
            <SimpleMenu options={options}>
              <MoreHoriz />
            </SimpleMenu>
          </>
        );
      }, 'another.action.navigation'),
    ];
  }, [
    // The columns don't read `rows`; rebuilding them after each load refreshes the
    // search, filters and handlers their cells close over.
    rows,
    userPermissions,
    isShowingUsers,
    isShowingForemen,
    hideDocumentTypesEnabled,
    showDriverTrailerSettingEnabled,
    saveDriverEquipment,
  ]);

  const userTypeFilters = [
    { value: UserTypesFilter.USERS, label: t('user.users') },
    { value: UserTypesFilter.INTERNAL_DRIVERS, label: t('user.internal_drivers') },
    { value: UserTypesFilter.FOREMEN, label: t('user.foremen') },
  ];

  const refreshCurrentTab = async () => {
    try {
      if (isShowingUsers) {
        await reloadUsersPage();
      } else if (isShowingForemen) {
        await reloadForemenPage();
      } else {
        await reloadDriversPage();
      }
    } catch {
      // Error toast is handled in the hooks.
    }
  };

  useEffect(() => {
    const refreshGridData = async () => {
      try {
        // every branch clears stale rows and cursors before fetching, so a failed fetch leaves none on screen
        if (isShowingUsers) {
          await startUsersOver();
        } else if (isShowingForemen) {
          await startForemenOver();
        } else {
          await startDriversOver();
        }
      } catch {
        // Error toast is handled in the hooks.
      }
    };

    void refreshGridData();
  }, [
    isShowingUsers,
    isShowingForemen,
    searchValue,
    userTypeFilter,
    companyId,
    isDriversPage,
    companyAssetsStore.driverFilters,
    companyAssetsStore.userFilters,
  ]);

  // This page already owns a filters row, so the actions ride it rather than
  // getting the standalone row the filter-less Settings pages use.
  const filtersRowSx = {
    alignItems: 'center',
    display: 'flex',
    gap: 2,
    justifyContent: 'space-between',
    mr: 3,
  };

  const gridActions = userPermissions.canCreateUser && (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <CSVImportExportButton onClick={csvModal.open} />
      <SmallButton
        color="brandV2Yellow"
        onClick={isDriversPage ? createNewDriver : createNewUser}
        startIcon={<Add />}
        disabled={!companyId}
      >
        {t(isDriversPage ? 'user.create_driver' : 'user.create_user')}
      </SmallButton>
    </Box>
  );

  return (
    <>
      <Box
        data-test-id={'users-data-grid'}
        sx={{
          width: '100%',
        }}
      >
        {rows && columns && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              // Reclaims the page's right gutter so the grid can span it; each
              // child puts the gutter back to keep its own right edge aligned.
              mr: -3,
            }}
          >
            {!isDriversPage ? (
              <>
                <StatusTabFilters
                  value={userTypeFilter}
                  onChange={(tab) => setUserTypeFilter(tab)}
                  filters={userTypeFilters}
                />
                <Box sx={filtersRowSx}>
                  {userTypeFilter === UserTypesFilter.USERS ? <UserFilters /> : <Box />}
                  {gridActions}
                </Box>
              </>
            ) : (
              <>
                <Typography variant="page-title" sx={{ mb: 2 }}>
                  {t('administration.drivers.title')}
                </Typography>

                <DriverAppShareBanner />

                <Box sx={filtersRowSx}>
                  <DriverFilters />
                  {gridActions}
                </Box>
              </>
            )}

            <Box sx={(theme) => ({ maxWidth: `calc(100% - ${theme.spacing(3)})` })}>
              <DataTable
                columns={columns}
                id="users-grid"
                loading={isGridLoading}
                onSearchChange={onChangeQuickFilter}
                rows={rows}
                headerTrailingComponent={
                  <DataGridPager
                    isFetching={isGridLoading}
                    onNavigate={(link) => {
                      if (isShowingUsers) {
                        void goToUsersPage(link);
                      } else if (isShowingForemen) {
                        void goToForemenPage(link);
                      } else {
                        void goToDriversPage(link);
                      }
                    }}
                    pagination={
                      isShowingUsers
                        ? usersPagination
                        : isShowingForemen
                          ? foremenPagination
                          : driversPagination
                    }
                  />
                }
              />
            </Box>
          </Box>
        )}
      </Box>
      <EquipmentDialog
        ref={sharedEquipmentDialogRef}
        isOpen={isSharedEquipmentDialogOpen}
        onClose={handleClose}
        selectedEquipment={selectedEquipment || null}
        companyShares={companyShares}
        companyId={companyId}
        isExternalEquipment={true}
        isUpdating={isUpdating}
        isUpdatingCompanyShares={isUpdatingCompanyShares}
        asyncErrors={asyncErrors}
        onSubmit={onSubmitSharedEquipmentForm}
        dialogTitle={t('administration.equipment.update_external_truck')}
      />
      <EquipmentDialog
        ref={equipmentDialogRef}
        isOpen={isEquipmentDialogOpen}
        onClose={handleClose}
        selectedEquipment={selectedEquipment || null}
        companyShares={companyShares}
        companyId={companyId}
        isExternalEquipment={false}
        isUpdating={isUpdating}
        isUpdatingCompanyShares={isUpdatingCompanyShares}
        asyncErrors={asyncErrors}
        onSubmit={onSubmitEquipmentForm}
        dialogTitle={t('administration.equipment.update_internal_truck')}
      />
      <Dialog isOpen={isDriverDialogOpen} onClose={handleClose}>
        <DialogHeader onClose={handleClose}>{t('user.create_driver')}</DialogHeader>

        <DialogContent>
          <DriverForm
            ref={driverDialogRef}
            driverContext="internal"
            errors={asyncErrors}
            inviteTypes={driverInviteType}
            inviteTypeError={driverInviteTypeError}
            isUpdating={isLoadingDrivers}
            onInviteTypesChange={onDriverInviteTypeChange}
            onFormStateChange={onDriverFormStateChange}
            onStagedDocsChange={setStagedComplianceDocs}
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isLoadingDrivers}
            onClick={handleClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            disabled={!formIsDirty || isLoadingDrivers}
            loading={isLoadingDrivers}
            onClick={onSubmitDriverForm}
          >
            {t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog
        isOpen={userFormDialogState.isOpen}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={handleClose}>
          {t(
            `user.${
              selectedId
                ? selectedDriver
                  ? `update_driver`
                  : `update_user`
                : 'create_user'
            }`,
          )}
        </DialogHeader>

        <DialogContent>
          <UserForm
            ref={updateUserDialogRef}
            allowSharing
            availableAuthMethods={userFormDialogState.availableAuthMethods}
            companyShares={companyShares}
            defaultDriver={selectedDriver}
            defaultUser={selectedId ? selectedUser : null}
            editable
            errors={asyncErrors}
            onFormStateChange={onFormStateChange}
            showInviteTypeSelector={!selectedId}
            inviteTypes={inviteTypes}
            inviteTypeError={inviteTypeError}
            onInviteTypesChange={onChangeInviteTypes}
          />

          {selectedUser?.id && (
            <UserNotificationsPreferenceForm
              key={selectedUser?.id}
              ref={userNotificationsPreferenceRef}
              onFormStateChange={onUserNotificationsPreferenceFormStateChange}
              user={selectedUser}
            />
          )}

          {(selectedDriver || hasDriverRole) && (
            <Box sx={{ mt: 3 }}>
              {selectedUser?.id || selectedDriver?.id ? (
                <CompliancePanel
                  compliantType={CompliantType.USER}
                  compliantId={(selectedUser?.id || selectedDriver?.id)!}
                  driverContext={selectedDriver?.companyShare ? 'shared' : 'internal'}
                  onComplianceChange={() => setComplianceIsDirty(true)}
                  onEditingStateChange={setIsComplianceEditing}
                />
              ) : (
                // Only render (and fetch) while the dialog is open. During the MUI close
                // transition the dialog stays mounted; clearing the selection would
                // otherwise flip us into staged mode and fire a spurious doc-types fetch.
                userFormDialogState.isOpen && (
                  <CompliancePanel
                    compliantType={CompliantType.USER}
                    driverContext="internal"
                    stagedMode
                    onStagedDocsChange={setStagedComplianceDocs}
                  />
                )
              )}
            </Box>
          )}

          {selectedId && !selectedDriver && invitesToSend && (
            <Box sx={{ mt: 2 }}>
              <InvitesToSendToggleGroup
                invitesToSend={invitesToSend}
                isUpdating={isUpdating}
                onChangeInvite={onChangeInvite}
              />
            </Box>
          )}

          {driverResendInvite && (
            <Box sx={{ mt: 2 }}>
              <InvitesToSendToggleGroup
                invitesToSend={{
                  email: {
                    shouldSend: driverResendSelection.email,
                    isDisabled: !driverResendInvite.canResendEmail,
                  },
                  sms: {
                    shouldSend: driverResendSelection.sms,
                    isDisabled: !driverResendInvite.canResendSms,
                  },
                }}
                isUpdating={isUpdating}
                label={t('user.resend_invitation')}
                onChangeInvite={onChangeDriverResend}
              />
              <SmallButton
                color="secondary"
                disabled={
                  isUpdating ||
                  (!driverResendSelection.email && !driverResendSelection.sms)
                }
                onClick={handleDriverResend}
                sx={{ mt: 1 }}
                variant="outlined"
              >
                {t('actions.send_now')}
              </SmallButton>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {Boolean(asyncErrors.length) && (
            <ShakyErrorIndicator
              message={t('administration.users.user_form.invalid_data_error_messages')}
            />
          )}

          <SmallButton
            color="secondary"
            disabled={isUpdating || isComplianceEditing}
            onClick={handleClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            disabled={isComplianceEditing}
            loading={isUpdating}
            onClick={onSubmitForm}
          >
            {t(`actions.${selectedId ? 'update' : 'create'}`)}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <DeactivateConfirmationDialog
        id={userIdToDelete}
        message={
          isShowingUsers || isShowingForemen
            ? t('administration.users.deactivate_user_dialog_message')
            : t('administration.drivers.deactivate_driver_dialog_message')
        }
        onClose={() => setUserIdToDelete(undefined)}
        onConfirm={handleDeleteUser}
        title={
          isShowingUsers || isShowingForemen
            ? t('administration.users.deactivate_user_dialog_title')
            : t('administration.drivers.deactivate_driver_dialog_title')
        }
      />
      <CSVImportExportModal
        isOpen={csvModal.isOpen}
        onClose={csvModal.close}
        config={csvConfig}
        loadConfig={loadCsvConfig}
        companyId={companyId || ''}
        entityDisplayName={
          isDriversPage ? t('page_headings.drivers') : t('form_fields.users')
        }
        onImportComplete={csvModal.handleImportComplete}
      />
    </>
  );
});
